/**
 * Route Definitions
 * @description All application routes with lazy loading and role-based access
 */

// Layouts
import DashboardLayout from "@/layout/DashboardLayout.vue";
import AuthLayout from "@/layout/AuthLayout.vue";

// User Roles
import { UserRoles } from "@/stores/authStore";

// Auth Pages (lazy loaded)
const LoginPage = () =>
  import(/* webpackChunkName: "auth" */ "@/pages/auth/LoginPage.vue");
const RegisterPage = () =>
  import(/* webpackChunkName: "auth" */ "@/pages/auth/RegisterPage.vue");

// Investor Pages (lazy loaded)
const InvestorDashboard = () =>
  import(/* webpackChunkName: "investor" */ "@/pages/investor/InvestorDashboard.vue");
const CompanyDetail = () =>
  import(/* webpackChunkName: "investor" */ "@/pages/investor/CompanyDetail.vue");
const PortfolioComparison = () =>
  import(/* webpackChunkName: "investor" */ "@/pages/investor/PortfolioComparison.vue");
const InvestorCashradarPage = () =>
  import(
    /* webpackChunkName: "investor" */ "@/pages/investor/CashradarPage.vue"
  );

// Entrepreneur Pages (lazy loaded)
const MetricsInput = () =>
  import(/* webpackChunkName: "entrepreneur" */ "@/pages/entrepreneur/MetricsInput.vue");
const EntrepreneurCashradarPage = () =>
  import(
    /* webpackChunkName: "entrepreneur" */ "@/pages/entrepreneur/CashradarPage.vue"
  );
const EntrepreneurRiskShieldPage = () =>
  import(
    /* webpackChunkName: "entrepreneur" */ "@/pages/entrepreneur/RiskShieldPage.vue"
  );
const EntrepreneurAffiliateDashboardPage = () =>
  import(
    /* webpackChunkName: "entrepreneur" */ "@/pages/entrepreneur/AffiliateDashboardPage.vue"
  );
const PartnerDashboardPage = () =>
  import(
    /* webpackChunkName: "entrepreneur" */ "@/pages/entrepreneur/PartnerDashboardPage.vue"
  );
const InvestorOverview = () =>
  import(/* webpackChunkName: "entrepreneur" */ "@/pages/entrepreneur/InvestorOverview.vue");
const CapTablePage = () =>
  import(/* webpackChunkName: "entrepreneur" */ "@/pages/entrepreneur/CapTablePage.vue");
const UsersPage = () =>
  import(/* webpackChunkName: "entrepreneur" */ "@/pages/UsersPage.vue");
const ControlcenterPage = () =>
  import(/* webpackChunkName: "entrepreneur" */ "@/pages/ControlcenterPage.vue");
const ManagementDnaPage = () =>
  import(/* webpackChunkName: "entrepreneur" */ "@/pages/ManagementDnaPage.vue");
const ContractManagementPage = () =>
  import(/* webpackChunkName: "entrepreneur" */ "@/pages/entrepreneur/ContractManagementPage.vue");
const CostPlanningPage = () =>
  import(/* webpackChunkName: "entrepreneur" */ "@/pages/entrepreneur/CostPlanningPage.vue");
const FinancialHealthPage = () =>
  import(/* webpackChunkName: "entrepreneur" */ "@/pages/entrepreneur/FinancialHealthPage.vue");
const MarketTractionPage = () =>
  import(/* webpackChunkName: "entrepreneur" */ "@/pages/entrepreneur/MarketTractionPage.vue");
const BankingPage = () =>
  import(/* webpackChunkName: "entrepreneur" */ "@/pages/entrepreneur/BankingPage.vue");
const PersonalplanungPage = () =>
  import(/* webpackChunkName: "entrepreneur" */ "@/pages/entrepreneur/PersonalplanungPage.vue");
const BuchhaltungSteuerPage = () =>
  import(/* webpackChunkName: "entrepreneur" */ "@/pages/entrepreneur/BuchhaltungSteuerPage.vue");
const LiquiditaetsmanagementPage = () =>
  import(/* webpackChunkName: "entrepreneur" */ "@/pages/entrepreneur/LiquiditaetsmanagementPage.vue");
const ForderungsmanagementPage = () =>
  import(/* webpackChunkName: "entrepreneur" */ "@/pages/entrepreneur/ForderungsmanagementPage.vue");
const MarktsichtbarkeitInflowPage = () =>
  import(/* webpackChunkName: "entrepreneur" */ "@/pages/entrepreneur/MarktsichtbarkeitInflowPage.vue");
const PipelineEffizienzSpeedPage = () =>
  import(/* webpackChunkName: "entrepreneur" */ "@/pages/entrepreneur/PipelineEffizienzSpeedPage.vue");
const VertriebsperformancePage = () =>
  import(/* webpackChunkName: "entrepreneur" */ "@/pages/entrepreneur/VertriebsperformancePage.vue");
const MarktresonanzRetentionPage = () =>
  import(/* webpackChunkName: "entrepreneur" */ "@/pages/entrepreneur/MarktresonanzRetentionPage.vue");
const KapitalmanagementPage = () =>
  import(/* webpackChunkName: "entrepreneur" */ "@/pages/entrepreneur/KapitalmanagementPage.vue");
const AusgabenmanagementPage = () =>
  import(/* webpackChunkName: "entrepreneur" */ "@/pages/entrepreneur/AusgabenmanagementPage.vue");
const PersonalkostenPage = () =>
  import(/* webpackChunkName: "entrepreneur" */ "@/pages/entrepreneur/PersonalkostenPage.vue");
const FluktuationPage = () =>
  import(/* webpackChunkName: "entrepreneur" */ "@/pages/entrepreneur/FluktuationPage.vue");
const PersonalproduktivitaetPage = () =>
  import(/* webpackChunkName: "entrepreneur" */ "@/pages/entrepreneur/PersonalproduktivitaetPage.vue");
const CashmanagementPage = () =>
  import(/* webpackChunkName: "entrepreneur" */ "@/pages/entrepreneur/CashmanagementPage.vue");
const KundenmanagementPage = () =>
  import(/* webpackChunkName: "entrepreneur" */ "@/pages/entrepreneur/KundenmanagementPage.vue");

// Investor Risk Shield Page (lazy loaded)
const InvestorRiskShieldPage = () =>
  import(
    /* webpackChunkName: "investor" */ "@/pages/investor/RiskShieldPage.vue"
  );
// Investor Affiliate Dashboard Page (lazy loaded)
const InvestorAffiliateDashboardPage = () =>
  import(
    /* webpackChunkName: "investor" */ "@/pages/investor/AffiliateDashboardPage.vue"
  );

const ChatPage = () =>
  import(/* webpackChunkName: "common" */ "@/pages/ChatPage.vue");

// Common Pages
const Profile = () =>
  import(/* webpackChunkName: "common" */ "@/pages/Profile.vue");

// Error Pages
const NotFoundPage = () =>
  import(/* webpackChunkName: "error" */ "@/pages/NotFoundPage.vue");

const routes = [
  // Auth Routes
  {
    path: "/auth",
    component: AuthLayout,
    meta: { isAuthPage: true },
    children: [
      {
        path: "login",
        name: "login",
        component: LoginPage,
      },
      {
        path: "register",
        name: "register",
        component: RegisterPage,
      },
    ],
  },

  // Investor Routes (Protected)
  {
    path: "/investor",
    component: DashboardLayout,
    meta: {
      requiresAuth: true,
      allowedRoles: [UserRoles.INVESTOR],
    },
    children: [
      {
        path: "",
        redirect: "dashboard",
      },
      {
        path: "dashboard",
        name: "investor-dashboard",
        component: InvestorDashboard,
        meta: { title: "Portfolio" },
      },
      {
        path: "cashradar",
        name: "investor-cashradar",
        component: InvestorCashradarPage,
        meta: { title: "Cashradar" },
      },
      {
        path: "risk-shield",
        name: "investor-risk-shield",
        component: InvestorRiskShieldPage,
        meta: { title: "Risk Shield" },
      },
      {
        path: "affiliate-dashboard",
        name: "investor-affiliate-dashboard",
        component: InvestorAffiliateDashboardPage,
        meta: { title: "Affiliate Dashboard" },
      },
      {
        path: "company/:id",
        name: "company-detail",
        component: CompanyDetail,
        meta: { title: "Unternehmen" },
        props: true,
      },
      {
        path: "compare",
        name: "portfolio-comparison",
        component: PortfolioComparison,
        meta: { title: "Vergleich" },
      },
      {
        path: "chat",
        name: "investor-chat",
        component: ChatPage,
        meta: { title: "Chat" },
      },
    ],
  },

  // Entrepreneur Routes (Protected)
  {
    path: "/entrepreneur",
    component: DashboardLayout,
    meta: {
      requiresAuth: true,
      allowedRoles: [UserRoles.ENTREPRENEUR],
    },
    children: [
      {
        path: "",
        redirect: "controlcenter",
      },
      {
        path: "financial-health",
        name: "financial-health",
        component: FinancialHealthPage,
        meta: { title: "Financial Health" },
      },
      {
        path: "liquiditaetsmanagement",
        name: "liquiditaetsmanagement",
        component: LiquiditaetsmanagementPage,
        meta: { title: "Liquiditätsmanagement" },
      },
      {
        path: "forderungsmanagement",
        name: "forderungsmanagement",
        component: ForderungsmanagementPage,
        meta: { title: "Forderungsmanagement" },
      },
      {
        path: "marktsichtbarkeit-inflow",
        name: "marktsichtbarkeit-inflow",
        component: MarktsichtbarkeitInflowPage,
        meta: { title: "Marktsichtbarkeit & Inflow" },
      },
      {
        path: "pipeline-effizienz-speed",
        name: "pipeline-effizienz-speed",
        component: PipelineEffizienzSpeedPage,
        meta: { title: "Pipeline-Effizienz & Speed" },
      },
      {
        path: "vertriebsperformance",
        name: "vertriebsperformance",
        component: VertriebsperformancePage,
        meta: { title: "Vertriebsperformance (Abschluss)" },
      },
      {
        path: "marktresonanz-retention",
        name: "marktresonanz-retention",
        component: MarktresonanzRetentionPage,
        meta: { title: "Marktresonanz & Retention" },
      },
      {
        path: "kapitalmanagement",
        name: "kapitalmanagement",
        component: KapitalmanagementPage,
        meta: { title: "Kapitalmanagement (Struktur)" },
      },
      {
        path: "ausgabenmanagement",
        name: "ausgabenmanagement",
        component: AusgabenmanagementPage,
        meta: { title: "Ausgabenmanagement" },
      },
      {
        path: "personalkosten",
        name: "personalkosten",
        component: PersonalkostenPage,
        meta: { title: "Personalkosten" },
      },
      {
        path: "fluktuation",
        name: "fluktuation",
        component: FluktuationPage,
        meta: { title: "Fluktuation" },
      },
      {
        path: "personalproduktivitaet",
        name: "personalproduktivitaet",
        component: PersonalproduktivitaetPage,
        meta: { title: "Personalproduktivität" },
      },
      {
        path: "controlcenter",
        name: "controlcenter",
        component: ControlcenterPage,
        meta: { title: "Zusammenfassung" },
      },
      {
        path: "management-dna",
        name: "management-dna",
        component: ManagementDnaPage,
        meta: { title: "Management DNA" },
      },
      {
        path: "market-traction",
        name: "market-traction",
        component: MarketTractionPage,
        meta: { title: "Market Traction" },
      },
      {
        path: "banking",
        name: "banking",
        component: BankingPage,
        meta: { title: "Banking" },
      },
      {
        path: "metrics",
        name: "metrics-input",
        component: MetricsInput,
        meta: { title: "Umsatzplanung" },
      },
      {
        path: "cost-planning",
        name: "cost-planning",
        component: CostPlanningPage,
        meta: { title: "Kostenplanung" },
      },
      {
        path: "personalplanung",
        name: "personalplanung",
        component: PersonalplanungPage,
        meta: { title: "Personalplanung" },
      },
      {
        path: "buchhaltung-steuer",
        name: "buchhaltung-steuer",
        component: BuchhaltungSteuerPage,
        meta: { title: "Buchhaltung & Steuer" },
      },
      {
        path: "contract-management",
        name: "contract-management",
        component: ContractManagementPage,
        meta: { title: "Vertragsmanagement" },
      },
      {
        path: "cashmanagement",
        name: "cashmanagement",
        component: CashmanagementPage,
        meta: { title: "Cashmanagement" },
      },
      {
        path: "kundenmanagement",
        name: "kundenmanagement",
        component: KundenmanagementPage,
        meta: { title: "Kundenmanagement" },
      },
      {
        path: "cashradar",
        name: "entrepreneur-cashradar",
        component: EntrepreneurCashradarPage,
        meta: { title: "Cashradar" },
      },
      {
        path: "risk-shield",
        name: "entrepreneur-risk-shield",
        component: EntrepreneurRiskShieldPage,
        meta: { title: "Risk Shield" },
      },
      {
        path: "affiliate-dashboard",
        name: "entrepreneur-affiliate-dashboard",
        component: EntrepreneurAffiliateDashboardPage,
        meta: { title: "Affiliate Dashboard" },
      },
      {
        path: "partner-dashboard",
        name: "partner-dashboard",
        component: PartnerDashboardPage,
        meta: { title: "Partner Dashboard" },
      },
      {
        path: "investors",
        name: "investor-overview",
        component: InvestorOverview,
        meta: { title: "Investoren" },
      },
      {
        path: "cap-table",
        name: "cap-table",
        component: CapTablePage,
        meta: { title: "Cap Table" },
      },
      {
        path: "users",
        name: "users-management",
        component: UsersPage,
        meta: { title: "Benutzer" },
      },
      {
        path: "chat",
        name: "entrepreneur-chat",
        component: ChatPage,
        meta: { title: "Chat" },
      },
    ],
  },

  // Common Routes (Any authenticated user)
  {
    path: "/",
    component: DashboardLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: "",
        name: "home",
        redirect: () => {
          // Redirect based on role - handled in router guard
          return "/investor/dashboard";
        },
      },
      {
        path: "profile",
        name: "profile",
        component: Profile,
        meta: { title: "Profil" },
      },
    ],
  },

  // Legacy redirects for backwards compatibility
  {
    path: "/dashboard",
    redirect: "/investor/dashboard",
  },
  {
    path: "/metriken",
    redirect: "/entrepreneur/metrics",
  },

  // 404 Catch-all
  {
    path: "*",
    component: NotFoundPage,
  },
];

export default routes;
